import {useCallback, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import * as ViewHooks from '../../../state/views/hooks';
import * as PanelActions from '../../../state/views/panel/actions';
import {Ref as PanelRef} from '../../../state/views/panel/types';
import {Ref as PanelBankConfigRef} from '../../../state/views/panelBankConfig/types';
import {PanelBankSectionConfigNormalized} from '../../../state/views/panelBankSectionConfig/types';
import {Dispatch} from '../../../types/redux';
import {LayedOutPanel} from '../../../util/panelTypes';
import {MediaBrowserPanelConfig} from '../common';

function getPanelRefsToUpdate(
  panelRefs: PanelRef[] | undefined,
  panels: LayedOutPanel[],
  panelSliderKeys: string[],
  stepSliderKey: string // the slider key we are trying to sync to
): PanelRef[] | undefined {
  // Only update panels that are media browser panels and have the same slider key
  return panelRefs?.filter((_panelRef, i) => {
    const panel = panels[i];
    if (panel?.viewType !== 'Media Browser') {
      return false;
    }

    const panelSliderKey = panelSliderKeys[i];
    if (panelSliderKey !== stepSliderKey) {
      return false;
    }
    return true;
  });
}

function syncPanelSteps(
  // All the panels that we potentially want to update
  panelRefs: PanelRef[] | undefined,
  panels: LayedOutPanel[],
  panelSliderKeys: string[],
  // Whether the slider sync is enabled
  sliderSync: boolean,
  // The new slider value
  newSliderValue: number,
  // The slider key corresponding to the panel that was just incremented
  panelStepSliderKey: string,
  // The bulk (workspace/section) slider key that sliderSync is enabled for the panel that was just incremented
  incrementedBulkSliderKey: string,
  dispatch: Dispatch
) {
  // Only do anything if the incremented panel's slider key matches the workspace/section's slider key.
  if (sliderSync && panelStepSliderKey === incrementedBulkSliderKey) {
    const panelRefsToBeUpdated = getPanelRefsToUpdate(
      panelRefs,
      panels,
      panelSliderKeys,
      panelStepSliderKey
    );
    if (panelRefsToBeUpdated) {
      dispatch(
        PanelActions.updateConfigs(panelRefsToBeUpdated, {
          stepIndex: newSliderValue,
        })
      );
    }
  }
}

export const useSetSectionSyncSliderSteps = (
  workspaceMediaSettings: MediaBrowserPanelConfig | undefined,
  sectionMediaSettings: MediaBrowserPanelConfig | undefined,
  section: NonNullable<PanelBankSectionConfigNormalized>,
  sectionPanels: LayedOutPanel[]
) => {
  const {mediaSliderKey: workspaceStepSliderKey = '_step'} =
    workspaceMediaSettings ?? {};
  const {
    sliderSync: sectionSliderSync = false,
    mediaSliderKey: sectionStepSliderKey = workspaceStepSliderKey,
  } = sectionMediaSettings ?? {};
  const sectionPanelRefs = section?.panelRefs;
  const panelSliderKeys = useMemo(
    () =>
      sectionSliderSync
        ? sectionPanels?.map(panel => {
            if (
              panel.viewType === 'Media Browser' &&
              panel.config.mediaSliderKey
            ) {
              return panel.config.mediaSliderKey;
            }
            return (
              section?.sectionSettings?.media?.mediaSliderKey ??
              workspaceStepSliderKey
            );
          })
        : [],
    [section, workspaceStepSliderKey, sectionPanels, sectionSliderSync]
  );
  const dispatch = useDispatch();

  return useCallback(
    (newSliderValue: number, panelStepSliderKey: string) => {
      syncPanelSteps(
        sectionPanelRefs,
        sectionPanels,
        panelSliderKeys,
        sectionSliderSync,
        newSliderValue,
        panelStepSliderKey,
        sectionStepSliderKey,
        dispatch
      );
    },
    [
      panelSliderKeys,
      sectionSliderSync,
      sectionPanelRefs,
      dispatch,
      sectionPanels,
      sectionStepSliderKey,
    ]
  );
};

export const useSetWorkspaceSyncSliderSteps = (
  workspaceMediaSettings: MediaBrowserPanelConfig | undefined,
  panelBankConfigRef: PanelBankConfigRef
) => {
  const {
    sliderSync: workspaceSliderSync = false,
    mediaSliderKey: workspaceStepSliderKey = '_step',
  } = workspaceMediaSettings ?? {};
  const panelBankConfig = ViewHooks.usePartMaybe(
    workspaceSliderSync ? panelBankConfigRef : null
  );
  const sections = ViewHooks.useParts(
    workspaceSliderSync ? (panelBankConfig?.sectionRefs ?? []) : []
  ).filter(
    s => s.sectionSettings?.media?.sliderSync === undefined // Filter out any sections that have a user defined sync slider value.
  );
  const sectionPanelRefs = useMemo(
    () =>
      workspaceSliderSync
        ? sections
            .map(s =>
              s.panelRefs.map(panelRef => ({
                inheritedSliderKey:
                  s.sectionSettings?.media?.mediaSliderKey ??
                  workspaceStepSliderKey,
                panelRef,
              }))
            )
            .flat()
        : [],
    [sections, workspaceStepSliderKey, workspaceSliderSync]
  );
  const panelRefs = useMemo(
    () => (workspaceSliderSync ? sectionPanelRefs.map(s => s.panelRef) : []),
    [sectionPanelRefs, workspaceSliderSync]
  );
  const panels = ViewHooks.useParts(panelRefs.flat());

  const dispatch = useDispatch();

  return useCallback(
    (
      newSliderValue: number,
      incrementedPanelSliderKey: string,
      sectionSliderSync: boolean
    ) => {
      const shouldDoAnything = !sectionSliderSync && workspaceSliderSync;
      const panelSliderKeys = shouldDoAnything
        ? sectionPanelRefs.map(({inheritedSliderKey}, index) => {
            const panel = panels[index];
            if (
              panel.viewType === 'Media Browser' &&
              panel.config.mediaSliderKey
            ) {
              return panel.config.mediaSliderKey;
            }
            return inheritedSliderKey;
          })
        : [];
      syncPanelSteps(
        panelRefs,
        panels,
        panelSliderKeys,
        shouldDoAnything, // Only sync other sections based on the workspace slider sync setting if the section is not also synced.
        newSliderValue,
        incrementedPanelSliderKey,
        workspaceStepSliderKey,
        dispatch
      );
    },
    [
      panelRefs,
      panels,
      workspaceSliderSync,
      dispatch,
      workspaceStepSliderKey,
      sectionPanelRefs,
    ]
  );
};
